import React, { useRef, useState } from "react";
import Avatar from "../common/Avatar";
import FifaVerifiedBadge from "../common/FifaVerifiedBadge";
import useTranslate from "../../hooks/useTranslate";
import shortRelativeTime from "./relativeTime";
import { YouChip } from "./LoungePostCard";
import { MentionText } from "./mentionText";
import LoungeKebabMenu from "./LoungeKebabMenu";
import LoungeInlineEdit from "./LoungeInlineEdit";
import LoungeReportedNotice from "./LoungeReportedNotice";

// One top-level comment with its single reply level (QAL-T4).
// Design: frame 1c (desktop comment thread), 1e·2 / 1l·2 (mobile detail).
//
// Tokens, straight from the frames:
//   * Desktop top-level: avatar 32, name 13.5/600, time 12.5 #9CA3AF,
//     text 13.5/1.55; reply level `margin-left:26px; padding-left:16px;
//     border-left:2px #EEF1F5`, avatar 26.
//   * Mobile: avatar 30 (workorder "mobil 30/26px"), card separators
//     (border-b #EEF1F5) instead of the desktop 18px gaps; reply level
//     `margin-left:20px; padding-left:12px`, avatar 26.
//   * Vote pill on comments: 26px desktop / 36px mobile — interactive
//     since T5 (same states as the post pill, incl. the L16 downvoted
//     fill — see VotePill in LoungePostCard.js).
//   * T6: kebab in the header row (1c Z.387/407 — 15px glyph, menu
//     min-width 150; own → Edit/Delete comment, foreign → Report), header
//     "· edited" stamp (comments get ONLY the header stamp — the design
//     draws no comment footer stamp, workorder-fixed), inline edit
//     (comment variant of 1m·2, no channel dropdown) and the reported
//     collapse (1h·2 wording in its comment variant). A reported node
//     collapses INCLUDING its replies — matching the server's hide-for-
//     reporter on reload (T4 rule: a hidden top-level hides its replies).
//   * Reply toggle — belegte Design-Divergenz L13, deliberately kept:
//     desktop = 24px dash + "View {n} replies"/"Hide replies", DEFAULT
//     OPEN; mobile = chevron + "View 1 reply"/"Hide reply", DEFAULT
//     CLOSED. `defaultRepliesOpen` carries the per-viewport default from
//     the page.
//   * Inline reply composer: the design has no frame for it (gap L8) —
//     MINIMAL fill: same optics as the bottom composer bar (pill input +
//     blue Comment button), indented to the reply level, prefilled with
//     "@{first name} ". Enter sends, Escape closes.

const UpChevron = ({ className = "" }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    className={`w-3 h-3 sm:w-[11px] sm:h-[11px] ${className}`}
  >
    <path
      d="M3.5 9.5L8 5l4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DownChevron = ({ className = "" }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    className={`w-3 h-3 sm:w-[11px] sm:h-[11px] ${className}`}
  >
    <path
      d="M3.5 6.5L8 11l4.5-4.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ToggleChevron = ({ open }) => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d={open ? "M3.5 10l4.5-4.5L12.5 10" : "M3.5 6l4.5 4.5L12.5 6"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Interactive comment vote pill (T5) — 26px desktop / 36px mobile (1c vs
// 1e·2). Same state colors as the post pill: upvoted = bg #EDF4FF + text
// #2283FF (1c Z.367); downvoted = the L16 gap fill from existing tokens
// (bg #F1F4F8 + text #111827, no new color). `onVote(value)`, value ∈
// {1,-1}; the page owns the optimistic state.
const CommentVotePill = ({ score, myVote = null, onVote, t }) => {
  const up = myVote === 1;
  const down = myVote === -1;
  return (
    <div
      data-testid="comment-vote-pill"
      className="inline-flex items-center h-9 sm:h-[26px] border border-[#E4E9F0] rounded-full bg-white select-none align-top overflow-hidden"
    >
      <button
        type="button"
        onClick={() => onVote && onVote(1)}
        aria-pressed={up}
        aria-label={t("Upvote")}
        className={`flex items-center gap-[5px] h-full pl-3 pr-2 sm:pl-[9px] sm:pr-[7px] text-[12.5px] sm:text-[12px] font-semibold border-none rounded-l-full cursor-pointer ${
          up
            ? "bg-[#EDF4FF] text-[#2283FF]"
            : "bg-transparent text-[#6B7280] hover:bg-[#F4F8FD] hover:text-[#2283FF]"
        }`}
      >
        <UpChevron />
        <span>{score}</span>
      </button>
      <span className="w-px h-[13px] bg-[#E4E9F0] shrink-0" />
      <button
        type="button"
        onClick={() => onVote && onVote(-1)}
        aria-pressed={down}
        aria-label={t("Downvote")}
        className={`flex items-center h-full px-[11px] sm:px-2 border-none rounded-r-full cursor-pointer ${
          down
            ? "bg-[#F1F4F8] text-[#111827]"
            : "bg-transparent text-[#9CA3AF] hover:bg-[#F4F8FD] hover:text-[#111827]"
        }`}
      >
        <DownChevron />
      </button>
    </div>
  );
};

const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";

// Header + text + action row — shared by top-level comments and replies.
// `onVote(value)` (T5) wires the pill; the page owns the optimistic state.
// `menu` (T6): the kebab element, rendered at the header's right edge.
const CommentBody = ({ comment, isOwn, onReplyClick, onVote, menu, t }) => {
  const author = comment?.author || {};
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-[5px] sm:gap-1.5 min-w-0">
        <span className="text-[13px] sm:text-[13.5px] font-semibold text-[#111827] whitespace-nowrap overflow-hidden text-ellipsis">
          {author.name}
        </span>
        {author.is_fifa_verified === true && <FifaVerifiedBadge size="sm" />}
        {isOwn && <YouChip label={t("You")} />}
        <span className="text-[12px] sm:text-[12.5px] text-[#9CA3AF] whitespace-nowrap shrink-0">
          · {shortRelativeTime(comment?.created_at)}
          {comment?.edited_at && <> · {t("edited")}</>}
        </span>
        <span className="flex-1" />
        {menu}
      </div>
      <p className="m-0 mt-1 mb-2 sm:mt-[5px] sm:mb-[9px] text-[13.5px] leading-[1.55] text-[#111827] whitespace-pre-wrap break-words">
        <MentionText text={comment?.text} />
      </p>
      <div className="flex items-center">
        <CommentVotePill
          score={comment?.score ?? 0}
          myVote={comment?.my_vote ?? null}
          onVote={onVote}
          t={t}
        />
        <button
          type="button"
          onClick={onReplyClick}
          className="inline-flex items-center h-9 sm:h-[26px] border-none bg-transparent text-[#6B7280] text-[12.5px] font-semibold cursor-pointer ml-2 sm:ml-2.5 px-2.5 sm:px-2 rounded-[7px] hover:bg-[#F1F4F8] hover:text-[#111827]"
        >
          {t("Reply")}
        </button>
      </div>
    </div>
  );
};

// Inline reply composer (design gap L8 — minimal, bottom-bar optics).
const ReplyComposer = ({ prefill, submitting, onSend, onClose, t }) => {
  const [text, setText] = useState(prefill);
  const inputRef = useRef(null);
  const canSend = text.trim().length > 0 && !submitting;

  const send = () => {
    if (canSend) onSend(text.trim());
  };

  return (
    <div
      data-testid="reply-composer"
      className="flex items-center gap-2 mt-2.5 sm:mt-3"
    >
      <input
        ref={(el) => {
          inputRef.current = el;
          if (el && !el.dataset.focused) {
            el.dataset.focused = "true";
            el.focus();
            // Caret behind the "@Name " prefill.
            el.setSelectionRange(el.value.length, el.value.length);
          }
        }}
        type="text"
        value={text}
        maxLength={1000}
        onChange={(e) => setText(e.target.value.slice(0, 1000))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            send();
          } else if (e.key === "Escape") {
            onClose();
          }
        }}
        aria-label={t("Reply")}
        className="flex-1 min-w-0 h-11 sm:h-9 border border-[#DDE3EB] rounded-full px-4 text-[13px] sm:text-[13.5px] text-[#111827] placeholder-[#9CA3AF] bg-white outline-none focus:border-[#2283FF] focus:shadow-[0_0_0_3px_rgba(34,131,255,0.14)]"
        data-testid="reply-input"
      />
      <button
        type="button"
        onClick={send}
        disabled={!canSend}
        className={`h-11 sm:h-9 bg-[#2283FF] text-white border-none rounded-[10px] px-3.5 text-[13px] font-semibold whitespace-nowrap shrink-0 ${
          canSend ? "hover:bg-[#1B6FE0] cursor-pointer" : "opacity-50 cursor-default"
        }`}
        data-testid="reply-send"
      >
        {t("Comment")}
      </button>
    </div>
  );
};

// `onVote(commentId, value)` (T5): one handler for the top-level comment
// AND its replies — the page patches whichever node the id belongs to.
// T6 wiring (all optional — pre-T6 callers render unchanged): the page owns
// which node is editing (`editingId`) / reported (`reportedIds` Set) and
// receives the kebab intents per node.
const LoungeComment = ({
  comment,
  currentAccountId,
  defaultRepliesOpen = true,
  highlightId,
  onSubmitReply,
  onVote,
  isLast = false,
  editingId = null,
  savingEdit = false,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteRequest,
  onReportRequest,
  reportedIds,
  onUndoReport,
  undoingId = null,
}) => {
  const { t, tv } = useTranslate();
  const [open, setOpen] = useState(defaultRepliesOpen);
  // Which comment the inline composer sits under (id) — null = closed.
  const [replyUnder, setReplyUnder] = useState(null);
  const [replyPrefill, setReplyPrefill] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const replies = Array.isArray(comment?.replies) ? comment.replies : [];
  const hasReplies = replies.length > 0;
  const isOwn = (c) =>
    currentAccountId != null &&
    c?.author?.account_id != null &&
    Number(c.author.account_id) === Number(currentAccountId);

  const hasModeration = Boolean(onStartEdit || onDeleteRequest || onReportRequest);
  const isReported = (id) => Boolean(reportedIds && reportedIds.has(id));
  const kebabFor = (node, parentId = null) =>
    hasModeration ? (
      <LoungeKebabMenu
        own={isOwn(node)}
        type="comment"
        size="comment"
        onEdit={() => onStartEdit && onStartEdit(node)}
        onDelete={() => onDeleteRequest && onDeleteRequest(node, parentId)}
        onReport={() => onReportRequest && onReportRequest(node)}
      />
    ) : null;
  const editorFor = (node) => (
    <LoungeInlineEdit
      type="comment"
      initialText={node?.text || ""}
      submitting={savingEdit}
      onCancel={onCancelEdit}
      onSave={({ text }) => onSaveEdit && onSaveEdit(node.id, text)}
    />
  );

  // Reply always targets the TOP-LEVEL comment as parent (single level,
  // server-enforced) — replying to a reply only changes the @prefill.
  const openReplyFor = (target) => {
    setReplyUnder(target.id);
    setReplyPrefill(`@${firstName(target?.author?.name)} `);
  };

  const sendReply = async (text) => {
    setSubmitting(true);
    const ok = await onSubmitReply(comment.id, text);
    setSubmitting(false);
    if (ok) {
      setReplyUnder(null);
      // A fresh reply must be visible even when the thread was folded.
      setOpen(true);
    }
  };

  const toggleLabel = open
    ? replies.length === 1
      ? t("Hide reply")
      : t("Hide replies")
    : replies.length === 1
      ? t("View 1 reply")
      : tv("View {{count}} replies", { count: replies.length });

  const highlightClass = (id) =>
    id === highlightId ? "bg-[#F8FBFF] transition-colors duration-500" : "";

  const composerFor = (id) =>
    replyUnder === id && (
      <ReplyComposer
        key={`composer-${id}-${replyPrefill}`}
        prefill={replyPrefill}
        submitting={submitting}
        onSend={sendReply}
        onClose={() => setReplyUnder(null)}
        t={t}
      />
    );

  // A reported top-level collapses WITH its replies to the confirmation
  // row (1h·2, comment copy) — Undo restores the whole node.
  if (isReported(comment.id)) {
    return (
      <div
        data-testid="lounge-comment"
        className={`${!isLast ? "border-b border-[#EEF1F5]" : ""} sm:border-0`}
      >
        <LoungeReportedNotice
          type="comment"
          card={false}
          onUndo={() => onUndoReport && onUndoReport(comment.id)}
          undoing={undoingId === comment.id}
        />
      </div>
    );
  }

  return (
    <div data-testid="lounge-comment" className="sm:flex sm:flex-col sm:gap-[18px]">
      {/* Top-level comment. Mobile: card row with separator (1e·2);
          desktop: free row in the 18px column (1c). */}
      <div
        id={`lounge-comment-${comment.id}`}
        className={`py-[13px] sm:py-0 ${
          !isLast || hasReplies ? "border-b border-[#EEF1F5]" : ""
        } sm:border-0 ${highlightClass(comment.id)}`}
      >
        <div className="flex gap-[9px] sm:gap-2.5">
          <div className="shrink-0">
            <span className="sm:hidden inline-block">
              <Avatar
                src={comment?.author?.profile_picture_url}
                alt={comment?.author?.name || ""}
                size={30}
                fallbackText={comment?.author?.name}
              />
            </span>
            <span className="hidden sm:inline-block">
              <Avatar
                src={comment?.author?.profile_picture_url}
                alt={comment?.author?.name || ""}
                size={32}
                fallbackText={comment?.author?.name}
              />
            </span>
          </div>
          <div className="flex-1 min-w-0">
            {editingId === comment.id ? (
              editorFor(comment)
            ) : (
              <CommentBody
                comment={comment}
                isOwn={isOwn(comment)}
                onReplyClick={() => openReplyFor(comment)}
                onVote={onVote ? (value) => onVote(comment.id, value) : undefined}
                menu={kebabFor(comment)}
                t={t}
              />
            )}
            {composerFor(comment.id)}
            {/* Desktop reply toggle — 24px dash + label, inside the
                comment content (1c). */}
            {hasReplies && editingId !== comment.id && (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="hidden sm:flex items-center gap-[9px] border-none bg-transparent text-[#6B7280] text-[12.5px] font-semibold cursor-pointer p-0 mt-3 hover:text-[#111827]"
                data-testid="replies-toggle-desktop"
              >
                <span className="w-6 h-px bg-[#C6CFDA]" />
                {toggleLabel}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile reply toggle — chevron + label, own row at avatar offset
          (1e·2: margin-left 39px). */}
      {hasReplies && (
        <div className="sm:hidden pl-[39px] pt-0.5 pb-1.5">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-[9px] border-none bg-transparent text-[#6B7280] text-[12.5px] font-semibold cursor-pointer py-1.5 min-h-[36px]"
            data-testid="replies-toggle-mobile"
          >
            {toggleLabel}
            <ToggleChevron open={open} />
          </button>
        </div>
      )}

      {/* Reply level — one level only. Desktop: ml 26 + pl 16, border-left
          2px #EEF1F5, avatar 26 (1c). Mobile: ml 20 + pl 12 (1e·2). */}
      {open &&
        replies.map((reply) =>
          isReported(reply.id) ? (
            <div
              key={reply.id}
              data-testid="lounge-reply"
              className="ml-5 pl-3 sm:ml-[26px] sm:pl-4 border-l-2 border-[#EEF1F5]"
            >
              <LoungeReportedNotice
                type="comment"
                card={false}
                onUndo={() => onUndoReport && onUndoReport(reply.id)}
                undoing={undoingId === reply.id}
              />
            </div>
          ) : (
            <div
              key={reply.id}
              id={`lounge-comment-${reply.id}`}
              data-testid="lounge-reply"
              className={`ml-5 pl-3 sm:ml-[26px] sm:pl-4 border-l-2 border-[#EEF1F5] pb-[13px] sm:pb-0 ${highlightClass(reply.id)}`}
            >
              <div className="flex gap-[9px] sm:gap-2.5">
                <div className="shrink-0">
                  <Avatar
                    src={reply?.author?.profile_picture_url}
                    alt={reply?.author?.name || ""}
                    size={26}
                    fallbackText={reply?.author?.name}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === reply.id ? (
                    editorFor(reply)
                  ) : (
                    <CommentBody
                      comment={reply}
                      isOwn={isOwn(reply)}
                      onReplyClick={() => openReplyFor(reply)}
                      onVote={onVote ? (value) => onVote(reply.id, value) : undefined}
                      menu={kebabFor(reply, comment.id)}
                      t={t}
                    />
                  )}
                  {composerFor(reply.id)}
                </div>
              </div>
            </div>
          ),
        )}
    </div>
  );
};

export default LoungeComment;
